<template>
  <form
    :key="activeTab"
    class="login-form"
    novalidate
    @keyup.enter="onSubmit"
    @submit.prevent="onSubmit"
  >
    <BaseInputPhone
      v-if="isActiveTabPhone"
      id="auth_phone"
      key="phone"
      v-model="phone"
      data-id="register-phone-input"
      focus
      :error-messages="errorMessages.phone"
      name="phone"
      :class="{ 'error': isActiveTabPhone && isErrorForm }"
      @input="errorMessages.phone = [], isErrorForm = false"
      @onValidate="v => isInternalValidPhone = v"
    />
    <BaseInputEmail
      v-if="isActiveTabEmail"
      id="auth_email"
      key="email"
      v-model="email"
      data-id="register-email-input"
      name="email"
      focus
      :error-messages="errorMessages.email"
      :error="isActiveTabEmail && isErrorForm"
      @input="errorMessages.email = [], isErrorForm = false"
    />
    <BaseInputPassword
      v-model="password"
      class="login-form__password"
      autocomplete="current-password"
      :name="isActiveTabEmail ? 'password_email' : 'password_phone'"
      :error-messages="errorMessages.password"
      :class="{ 'error': isErrorForm }"
      @input="errorMessages.password = [], isErrorForm = false"
    />
    <UiLink
      type="button"
      class="reg-auth-forgot__recovery-link"
      data-id="recovery-password-click"
      color="green"
      bold
      :text="$t('regAuthForgot.passwordRecovery.link')"
      @click="toPassRecovery"
    />
    <UiBtn
      type="submit"
      color="primary"
      full-width
      class="login-form__submit"
      data-id="reg-auth-submit-click"
      :disabled="formHasDisplayedErrors || isSubmitDisabled || isErrorForm"
      :loading="isSubmitDisabled"
      :text="$t('regAuthForgot.logIn')"
    />
    <RegAuthToggle v-if="showRegAuthToggle" />
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseInput from '@/components/Base/Input'
import { validationMixin } from 'vuelidate'
import { required, email, minLength } from 'vuelidate/lib/validators'
import {
  phoneValidationErrorMessage,
  emailValidationErrorMessage,
  passwordValidationErrorMessage
} from '@/util/inputValidatonErrorMessage'
import { AuthBy } from '@/util/composables/useLocalStorage'
import BaseInputPassword from '~/components/Base/Input/Password'
import BaseInputPhone from '~/components/Base/Input/Phone'
import BaseInputEmail from '~/components/Base/Input/Email'
import RegAuthToggle from '@/components/BigModal/RegAuthForgot/RegAuthToggle'

const initErrors = () => ({
  phone: [],
  email: [],
  password: [],
})

export default {
  name: 'LoginForm',
  components: {
    BaseInput,
    BaseInputPassword,
    BaseInputPhone,
    BaseInputEmail,
    RegAuthToggle,
  },
  mixins: [validationMixin],
  validations() {
    const commonFields = { password: { required, minLength: minLength(6) } }
    if (this.isActiveTabEmail) {
      return {
        email: { required, email },
        ...commonFields,
      }
    }
    else {
      return {
        phone: {
          tel: {
            required,
            custom() {
              return this.isInternalValidPhone
            },
          },
        },
        ...commonFields,
      }
    }
  },
  data() {
    return {
      AuthBy: {
        value: '',
      },
      phone: { code: '', tel: '' },
      email: '',
      password: '',
      /** error handle */
      isSubmitDisabled: false,
      errorMessages: initErrors(),
      isErrorForm: false,
      isInternalValidPhone: false,
    }
  },
  computed: {
    ...mapGetters('login', ['get_loginErrors']),
    ...mapGetters('user', ['userId', 'userExistsData']),
    isActiveTabPhone() {
      return this.activeTab === 'phone'
    },
    isActiveTabEmail() {
      return this.activeTab === 'email'
    },
    activeTab() {
      return this.$route.query.bigModalTab
    },
    formHasDisplayedErrors() {
      return Boolean(
        this.errorMessages.phone.length
        || this.errorMessages.email.length
        || this.errorMessages.password.length
      )
    },
    showRegAuthToggle() {
      return (
        this.$route.query?.bigModal === 'auth'
      )
    },
  },
  watch: {
    activeTab: 'resetErrors',
  },
  created() {
    if (process.client) {
      this.AuthBy = AuthBy

      if (Object.keys(this.userExistsData).length) {
        this.email = this.userExistsData?.email
        this.phone = { code: this.userExistsData?.code, tel: this.userExistsData?.tel }
      }
    }
  },
  beforeDestroy() {
    this.close_toaster()
  },
  methods: {
    ...mapActions('login', ['act_login']),
    ...mapActions('notifyToast', ['show_notify', 'close_toaster']),
    phoneValidationErrorMessage,
    emailValidationErrorMessage,
    passwordValidationErrorMessage,
    resetErrors() {
      this.errorMessages = initErrors()
      this.$v.$reset()
      this.isErrorForm = false
    },
    updateErrors() {
      this.errorMessages.phone = []
      this.errorMessages.email = []
      this.errorMessages.password = []
      if (this.$v.$invalid) {
        if (this.$v?.phone?.$error) {
          this.errorMessages.phone = this.phoneValidationErrorMessage(this.$v.phone.tel)
        }
        if (this.$v?.email?.$error) {
          this.errorMessages.email = this.emailValidationErrorMessage(this.$v.email)
        }
        if (this.$v?.password?.$error) {
          this.errorMessages.password = this.passwordValidationErrorMessage(this.$v.password)
        }
      }
    },
    onSubmit() {
      if (this.isSubmitDisabled || this.formHasDisplayedErrors) return
      this.isSubmitDisabled = true
      this.$store.dispatch('gtm/push', {
        vCategory: 'modal_signin',
        vAction: 'signin_click_send',
      })
      this.$v.$touch()
      this.updateErrors()
      if (this.$v.$invalid) {
        this.isSubmitDisabled = false
        return
      }
      this.login()
    },
    async login() {
      const payload = {
        password: this.password,
        type: this.activeTab,
      }
      if (this.isActiveTabEmail) {
        payload.email = this.email
      }
      else {
        payload.phone_code = this.phone.code
        payload.phone = this.phone.tel.replace(/-|\s/g, '')
      }

      try {
        const resp = await this.act_login(payload)
        this.$gtm.push({ event: 'FE_Auth', user_id: this.userId })
        this.isSubmitDisabled = false
        this.closeBigModal()
        AuthBy.value = this.isActiveTabEmail ? 'email' : 'phone'
      }
      catch (error) {
        if (this.get_loginErrors.password === 'bad_password') {
          this.errorMessages.password.push(this.$t('regAuthForgot.errors.maxPassword'))
          return
        }
        if (error.errorMessage === 'Precondition Failed') {
          this.isErrorForm = true
          this.show_notify({
            text: this.activeTab === 'phone' ? this.$t('regAuthForgot.errors.phonePass') : this.$t('regAuthForgot.errors.emailPass'),
            type: 'error',
          })

          setTimeout(() => {
            this.isErrorForm = false
          }, 7000)

          this.isActiveTabEmail
            ? this.$store.commit('user/userExistsData', { email: this.email })
            : this.$store.commit('user/userExistsData', { tel: this.phone.tel, code: this.phone.code })
        }
      }
      finally {
        this.isSubmitDisabled = false
      }
    },
    toPassRecovery() {
      this.$newModal.changeQueryParam('bigModal', 'resetPassword')
    },
    closeBigModal() {
      this.$newModal.closeBigModal()
    },
  },
}
</script>

<style lang="scss" src="./style.scss" scoped></style>
